import { Button, Loader, Modal, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageViewerModal } from 'src/Components/Modals/ImageViewerModal';
import useFetchMemberPhotoAlbum from 'src/Hooks/Queries/useFetchMemberPhotoAlbum';

import styles from './MemberPhotoAlbumModal.module.scss';
import { Props } from './MemberPhotoAlbumModal.types';

const MemberPhotoAlbumModal = ({ memberId, memberName, opened, onClose }: Props) => {
  const { t } = useTranslation();
  // Only fetch the album once the modal is opened: this component is mounted (closed)
  // for every member of the "Albums photos" section.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFetchMemberPhotoAlbum(opened ? memberId : undefined);

  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const photos = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.photos);
  }, [data]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t('photoTags.photosOf', 'Photos de {{name}}', { name: memberName })}
        size="lg"
      >
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadMore}>
              <Loader />
            </div>
          ) : photos.length > 0 ? (
            <>
              <div className={styles.grid}>
                {photos.map(photo => (
                  <img
                    key={photo.storageFileId}
                    src={photo.thumbnailUrl || photo.previewUrl || photo.url}
                    className={styles.thumbnail}
                    onClick={() => setSelectedImageUrl(photo.previewUrl || photo.url)}
                  />
                ))}
              </div>
              {hasNextPage && (
                <div className={styles.loadMore}>
                  <Button
                    variant="subtle"
                    onClick={() => fetchNextPage()}
                    loading={isFetchingNextPage}
                  >
                    {t('common.loadMore', 'Charger plus')}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Text c="dimmed" className={styles.emptyState}>
              {t('photoTags.noPhotos', 'Aucune photo')}
            </Text>
          )}
        </div>
      </Modal>

      <ImageViewerModal
        open={!!selectedImageUrl}
        imageUrl={selectedImageUrl || ''}
        onClose={() => setSelectedImageUrl(null)}
      />
    </>
  );
};

export default MemberPhotoAlbumModal;
